import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { getPodcasts } from './services/supabase/podcasts';
import { logout, subscribeToNewsletter, setupAuthRedirects } from './services/api';
import { Podcast } from './types/index';
import { PodcastCard } from './components/podcast/PodcastCard';
import { LoginForm } from './components/auth/LoginForm';
import './styles/animations.css';
import reality from './assets/reality.svg';
import expectation from './assets/expectation.svg';
import ScrollImage from "./components/ui/ScrollImage";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "./components/ui/carousel"
import Features from "./components/ui/Features"
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/toaster';
import { FeaturedIdeasCarousel } from './components/FeaturedIdeasCarousel';
import { FeaturedInsightsCarousel } from './components/FeaturedInsightsCarousel';
import { getAndClearReturnPath } from './lib/auth';
import { AuthCallback } from './components/auth/AuthCallback';
import { SuggestPodcastForm } from './components/SuggestPodcastForm';
import ScrollToTop from './components/ScrollToTop';
import AnalyticsTracker from './components/analytics-tracker';
import { useToast } from './components/ui/use-toast';
// import { FeedbackWidget } from './components/feedback/FeedbackWidget';
import { BugReportDialog } from './components/bug-report';
import { HelmetProvider } from 'react-helmet-async';

const PodcastPage = lazy(() => import('./pages/PodcastPage').then((module) => ({ default: module.PodcastPage })));
const PodcastChatPage = lazy(() => import('./pages/PodcastChatPage').then((module) => ({ default: module.PodcastChatPage })));
const EpisodePage = lazy(() => import('./pages/EpisodePage').then((module) => ({ default: module.EpisodePage })));
const EpisodeChatPage = lazy(() => import('./pages/EpisodeChatPage').then((module) => ({ default: module.EpisodeChatPage })));
const PodcastsPage = lazy(() => import('./pages/PodcastsPage').then((module) => ({ default: module.PodcastsPage })));
const ExploreIdeasPage = lazy(() => import('./pages/ExploreIdeasPage').then((module) => ({ default: module.ExploreIdeasPage })));
const SavedIdeasPage = lazy(() => import('./pages/SavedIdeasPage').then((module) => ({ default: module.SavedIdeasPage })));
const EpisodeSearchPage = lazy(() => import('./pages/EpisodeSearchPage').then((module) => ({ default: module.EpisodeSearchPage })));
const IdeaDetailsPage = lazy(() => import('./pages/IdeaDetailsPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));

// Prevent duplicate mobile warning toasts in React StrictMode
let mobileWarningToastShown = false;
// Utility function to detect mobile devices
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
  </div>
);

// Helper component for redirecting old podcast URLs
const PodcastRedirect = () => {
  const { nameOrId } = useParams();
  // This is a simplified redirect. 
  // A robust solution would involve looking up the podcast slug from the nameOrId.
  // For now, we'll assume the nameOrId can be used in the new structure,
  // and the PodcastPage will handle the canonical redirect.
  return <Navigate to={`/podcasts/${nameOrId}`} replace />;
};

// Helper component for redirecting old episode URLs
const EpisodeRedirect = () => {
  const { episodeId } = useParams();
  // Similar to PodcastRedirect, this is a simplified approach.
  // The EpisodePage will be responsible for redirecting to the full canonical URL.
  return <Navigate to={`/episodes/${episodeId}`} replace />;
};



function AppContent() {
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const { toast } = useToast();
  const podcastsFetchedRef = useRef(false);

  useEffect(() => {
    const fetchPodcasts = async () => {
      // Prevent duplicate calls in React StrictMode
      if (podcastsFetchedRef.current) {
        return;
      }

      try {
        podcastsFetchedRef.current = true;
        setLoading(true);
        setError(null);
        const data = await getPodcasts();
        setPodcasts(data);
      } catch (err) {
        const errorMessage = err instanceof Error
          ? err.message
          : 'Failed to fetch podcasts. Please try again later.';
        setError(`Error: ${errorMessage}`);
        console.error('Error fetching podcasts:', err);
        // Reset the ref on error so user can retry
        podcastsFetchedRef.current = false;
      } finally {
        setLoading(false);
      }
    };

    fetchPodcasts();
  }, []);

  const handleLogout = async () => {
    try {
      // Get current location before logout
      const currentPath = window.location.pathname;

      await logout();

      // Redirect to current page after successful logout
      window.location.href = currentPath;
    } catch (error) {
      console.error('Logout failed:', error);

      // Even if there's an error, still redirect to current page
      const currentPath = window.location.pathname;
      window.location.href = currentPath;
    }
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) return;

    try {
      setSubscriptionStatus('idle'); // Reset status

      // Call the API to subscribe the user
      await subscribeToNewsletter(email, 'footer');

      // Handle success
      setSubscriptionStatus('success');
      setEmail('');

      // Reset status after 3 seconds
      setTimeout(() => {
        setSubscriptionStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('Subscription failed:', error);
      setSubscriptionStatus('error');

      // Reset error status after 3 seconds
      setTimeout(() => {
        setSubscriptionStatus('idle');
      }, 3000);
    }
  };

  // Show mobile warning toast only once
  useEffect(() => {
    if (isMobileDevice() && !mobileWarningToastShown) {
      mobileWarningToastShown = true;
      toast({
        title: "Mobile View Detected",
        description: "For the best experience, we recommend using TalkToPods on a desktop device.",
        duration: 6000,
      });
    }
  }, [toast]);

  return (
    <div className="min-h-screen bg-[#F5F5F2]">
      <nav className="px-8 py-6 border-b border-gray-200 bg-[#F5F5F2] shadow-sm sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Link to="/" className="text-4xl font-averia-bold tracking-tight">
                talktopods
                <span className="inline-block font-sans w-[0.5em] animate-blink">_</span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/ideas" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors">
                All Ideas
              </Link>
              <Link to="/podcasts" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors">
                All Podcasts
              </Link>
              <Link to="/search" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors flex items-center gap-1">
                <Search className="h-4 w-4" />
                Search
              </Link>
              {user && (
                <Link
                  to="/ideas/saved"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors"
                  onClick={() => {
                    console.log('Saved Ideas link clicked, navigating to /ideas/saved');
                  }}
                >
                  Saved Ideas
                </Link>
              )}

              <Link to="/about" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors">
                About
              </Link>
              <Link to="/team" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors">
                Team
              </Link>
              <button
                onClick={() => setBugReportOpen(true)}
                className="text-white bg-gradient-to-r from-[#cb9ea3] to-[#e3b977] hover:from-[#b88d92] hover:to-[#d4a865] px-4 py-2 rounded-full text-sm font-medium transition-all shadow-md hover:shadow-lg transform hover:scale-105"
              >
                Report Bug
              </button>
              {user ? (
                <button
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors"
                >
                  Logout
                </button>
              ) : (
                <Link to="/login" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors">
                  Login
                </Link>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-600 hover:text-gray-900 focus:outline-none"
                aria-label="Toggle menu"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {mobileMenuOpen ? (
                    // X icon when menu is open
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    // Hamburger icon when menu is closed
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4 space-y-2">
              <Link
                to="/ideas"
                className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                All Ideas
              </Link>
              <Link
                to="/podcasts"
                className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                All Podcasts
              </Link>
              <Link
                to="/search"
                className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Search
              </Link>
              {user && (
                <Link
                  to="/ideas/saved"
                  className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors"
                  onClick={() => {
                    console.log('Saved Ideas link clicked, navigating to /ideas/saved');
                    setMobileMenuOpen(false);
                  }}
                >
                  Saved Ideas
                </Link>
              )}
              <Link
                to="/about"
                className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
              <Link
                to="/team"
                className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Team
              </Link>
              <button
                onClick={() => {
                  setBugReportOpen(true);
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left text-white bg-gradient-to-r from-[#cb9ea3] to-[#e3b977] px-3 py-2 rounded-lg text-sm font-medium transition-all mt-2"
              >
                🐛 Report Bug
              </button>
              {user ? (
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors"
                >
                  Logout
                </button>
              ) : (
                <Link
                  to="/login"
                  className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-sm transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
              )}
            </div>
          )}
        </div>
      </nav>

      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Routes without footer */}
          <Route path="/podcasts/:podcastSlug/chat" element={<PodcastChatPage />} />
          <Route path="/episodes/:episodeSlug/chat" element={<EpisodeChatPage />} />

          <Route
            path="/login"
            element={
              user ? (
                (() => {
                  // Check if auth callback is in progress - if so, don't redirect from here
                  const authInProgress = sessionStorage.getItem('authCallbackInProgress');
                  if (authInProgress === 'true') {
                    console.log('Login route: Detected auth callback in progress, deferring redirect');
                    return (
                      <div className="min-h-screen flex items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                      </div>
                    );
                  }

                  // Check if there's a final auth destination set by the AuthCallback component
                  const finalDestination = sessionStorage.getItem('finalAuthDestination');
                  if (finalDestination) {
                    console.log('Login route: Using final auth destination:', finalDestination);
                    // Clear the final destination to prevent it from being used again
                    sessionStorage.removeItem('finalAuthDestination');
                    return <Navigate to={finalDestination} replace />;
                  }

                  // Otherwise proceed with normal redirect logic
                  const params = new URLSearchParams(window.location.search);
                  const returnTo = params.get('returnTo') || getAndClearReturnPath() || '/';

                  // console.log('Login redirect with correct navigation:', {
                  //   user: !!user,
                  //   returnTo,
                  //   searchParams: Object.fromEntries(params.entries()),
                  //   localStorage: localStorage.getItem('authReturnPath'),
                  //   authInProgress
                  // });

                  return <Navigate to={returnTo} replace />;
                })()
              ) : (
                <LoginForm />
              )
            }
          />

          <Route
            path="/auth/callback"
            element={<AuthCallback />}
          />


          {/* Routes with footer wrapped in layout */}
          <Route path="/" element={
            <>
              {loading ? (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                </div>
              ) : error ? (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="bg-white p-6 rounded-lg shadow-lg max-w-md text-center">
                    <p className="text-gray-700 mb-4">{error}</p>
                    <button
                      onClick={() => window.location.reload()}
                      className="bg-gray-900 text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <section className="px-8 py-20">
                    <div className="max-w-[1400px] mx-auto">
                      <div className="max-w-3xl mb-20 mx-auto text-center">
                        <h1 className="text-6xl font-averia-bold tracking-tight mb-8">
                          Don't just listen. Talk to your podcasts.
                        </h1>
                        <p className="text-xl text-gray-600 mb-8">
                          Turn passive listening into active conversation. TalkToPods lets you chat with your favorite business podcasts to find specific answers, deep-dive into topics, and extract insights instantly.
                        </p>

                        <div className="flex items-center justify-center gap-6 mb-12">
                          <a
                            href="/podcasts"
                            className="inline-flex items-center px-6 py-3 rounded-full bg-black text-white hover:opacity-90"
                          >
                            Start Chatting →
                          </a>
                          <a
                            href="/podcasts"
                            className="inline-flex items-center px-6 py-3 rounded-full bg-white text-black hover:opacity-90"
                          >
                            Browse Podcasts →
                          </a>
                        </div>
                      </div>


                      <section>
                        <h2 className="text-[2.75rem] font-averia-bold mb-12 tracking-tight">The Reality Check</h2>
                        <div className="space-y-6">
                          <div className="grid md:grid-cols-[1fr,auto] gap-8 items-center">
                            <div className="bg-[#e3b977] rounded-2xl p-8">
                              <h3 className="text-2xl font-averia-bold mb-6">Expectation</h3>
                              <p className="text-xl leading-relaxed">
                                You listen to a podcast → The host mentions a game-changing strategy → You nod along feeling like a genius → You vow to implement it immediately.
                              </p>
                            </div>
                            <ScrollImage
                              src={expectation}
                              width={300}
                              height={200}
                              alt="Success illustration"
                              className="mx-auto md:sticky md:top-3 max-w-[250px] md:max-w-[300px]"
                            />
                          </div>
                          <div className="grid md:grid-cols-[auto,1fr] gap-8 items-center">
                            <ScrollImage
                              src={reality}
                              width={300}
                              height={200}
                              alt="Reality illustration"
                              className="mx-auto md:sticky md:top-3 max-w-[250px] md:max-w-[300px]"
                            />
                            <div className="bg-[#bab3cd] rounded-2xl p-8">
                              <h3 className="text-2xl font-averia-bold mb-6">Reality</h3>
                              <p className="text-xl leading-relaxed">
                                You listen to a podcast → You have a burning question 2 minutes later → The host has already moved on → You forget the context → You go back to doom scrolling and the insight is lost forever.
                              </p>
                            </div>
                          </div>
                        </div>
                      </section>

                      <section>
                        <h2 className="text-[2.75rem] font-averia-bold mb-12 tracking-tight">The Solution</h2>
                        <div className="grid md:grid-cols-[1fr,auto] gap-8 items-start">
                          <div className="space-y-12">
                            <div className="bg-[#cb9ea3] rounded-2xl p-8">
                              <h3 className="text-2xl font-semibold mb-6">Your Personal Podcast Companion</h3>
                              <div className="space-y-6">
                                <p className="text-xl leading-relaxed">
                                  TalkToPods gives you a direct line to the content. It’s like having the podcast host on speed dial, ready to answer any question about any episode.
                                </p>
                                <p className="text-xl leading-relaxed">
                                  Want to know exactly what they said about 'Unit Economics'? Just ask. Need a summary of their marketing advice? It's one message away.
                                </p>
                                <p className="text-xl leading-relaxed">
                                  Stop scrubbing through 2-hour episodes looking for that one specific nugget. Just ask TalkToPods, and get the answer instantly.
                                </p>
                              </div>
                            </div>


                          </div>
                          {/* <ScrollImage
                    src={sol_meme}
                    alt="Solution illustration"
                    className="mx-auto md:sticky md:top-8 max-w-[250px] md:max-w-[300px]"
                  /> */}
                        </div>
                      </section>
                    </div>
                    <div className="space-y-16">
                      {/* Featured Ideas Carousel */}
                      <FeaturedIdeasCarousel className="mb-20 mt-10" />

                      {/* Featured Insights Carousel */}
                      <FeaturedInsightsCarousel className="mb-20" />

                      {/* Latest Podcasts Carousel */}
                      <section>
                        <h2 className="text-[2.75rem] font-averia-bold mb-12 tracking-tight text-center">Latest Podcasts</h2>
                        <div className="relative w-full pb-12">
                          <Carousel
                            className="w-full"
                            opts={{
                              align: 'start',
                              loop: true,
                            }}
                          >
                            <CarouselContent className="-ml-4">
                              {!loading && Array.isArray(podcasts) && podcasts.length > 0 ? (
                                podcasts.map((podcast) => (
                                  <CarouselItem key={podcast.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                                    <div className="p-1">
                                      <PodcastCard podcast={podcast} isAuthenticated={!!user} />
                                    </div>
                                  </CarouselItem>
                                ))
                              ) : (
                                <CarouselItem className="pl-4">
                                  <div className="p-4 text-center text-gray-600">
                                    {loading ? "Loading podcasts..." : "No podcasts available at the moment"}
                                  </div>
                                </CarouselItem>
                              )}
                            </CarouselContent>
                            {!loading && Array.isArray(podcasts) && podcasts.length > 0 && (
                              <div className="flex justify-center gap-4 mt-8">
                                <CarouselPrevious />
                                <CarouselNext />
                              </div>
                            )}
                          </Carousel>
                        </div>
                      </section>

                      <div className="flex items-center mt-6 space-x-1 text-sm text-gray-500">
                      </div>

                      <Features />



                      <div className="mt-20 text-center">
                        <div className="flex justify-center gap-4 mb-12">
                          <a
                            href="/podcasts"
                            className="inline-flex items-center px-6 py-3 rounded-full bg-black text-white hover:opacity-90 transition-colors"
                          >
                            Explore Podcasts
                          </a>
                          <a
                            href="/ideas"
                            className="inline-flex items-center px-6 py-3 rounded-full border-2 border-black text-black hover:bg-black hover:text-white transition-colors"
                          >
                            Explore Ideas
                          </a>
                        </div>
                      </div>
                      {/* Suggest a Podcast Form */}
                      <div>
                        <SuggestPodcastForm />
                      </div>
                    </div>
                  </section>
                  <NewsletterSection email={email} setEmail={setEmail} subscriptionStatus={subscriptionStatus} handleSubscribe={handleSubscribe} />
                  <Footer />
                </>
              )}
            </>
          } />

          <Route path="/podcasts" element={
            <>
              <PodcastsPage />
              <NewsletterSection email={email} setEmail={setEmail} subscriptionStatus={subscriptionStatus} handleSubscribe={handleSubscribe} />
              <Footer />
            </>
          } />
          <Route path="/podcasts/:podcastSlug" element={
            <>
              <PodcastPage />
              <NewsletterSection email={email} setEmail={setEmail} subscriptionStatus={subscriptionStatus} handleSubscribe={handleSubscribe} />
              <Footer />
            </>
          } />
          <Route path="/podcast/:nameOrId" element={<PodcastRedirect />} />
          <Route path="/episodes/:episodeSlug" element={
            <>
              <EpisodePage />
              <NewsletterSection email={email} setEmail={setEmail} subscriptionStatus={subscriptionStatus} handleSubscribe={handleSubscribe} />
              <Footer />
            </>
          } />
          <Route path="/episode/:episodeId" element={<EpisodeRedirect />} />
          <Route path="/ideas" element={
            <>
              <ExploreIdeasPage />
              <NewsletterSection email={email} setEmail={setEmail} subscriptionStatus={subscriptionStatus} handleSubscribe={handleSubscribe} />
              <Footer />
            </>
          } />
          <Route path="/ideas/saved" element={
            <>
              <SavedIdeasPage />
              <NewsletterSection email={email} setEmail={setEmail} subscriptionStatus={subscriptionStatus} handleSubscribe={handleSubscribe} />
              <Footer />
            </>
          } />
          <Route path="/ideas/:ideaSlug" element={
            <>
              <IdeaDetailsPage />
              <NewsletterSection email={email} setEmail={setEmail} subscriptionStatus={subscriptionStatus} handleSubscribe={handleSubscribe} />
              <Footer />
            </>
          } />
          <Route path="/about" element={
            <>
              <AboutPage />
              <NewsletterSection email={email} setEmail={setEmail} subscriptionStatus={subscriptionStatus} handleSubscribe={handleSubscribe} />
              <Footer />
            </>
          } />
          <Route path="/team" element={
            <>
              <TeamPage />
              <NewsletterSection email={email} setEmail={setEmail} subscriptionStatus={subscriptionStatus} handleSubscribe={handleSubscribe} />
              <Footer />
            </>
          } />
          <Route path="/search" element={
            <>
              <EpisodeSearchPage />
              <NewsletterSection email={email} setEmail={setEmail} subscriptionStatus={subscriptionStatus} handleSubscribe={handleSubscribe} />
              <Footer />
            </>
          } />
        </Routes>
      </Suspense>
      <BugReportDialog isOpen={bugReportOpen} onClose={() => setBugReportOpen(false)} />
    </div>
  );
}

// Newsletter Section Component
function NewsletterSection({ email, setEmail, subscriptionStatus, handleSubscribe }: {
  email: string;
  setEmail: (email: string) => void;
  subscriptionStatus: 'idle' | 'success' | 'error';
  handleSubscribe: (e: React.FormEvent) => void;
}) {
  return (
    <section className="py-16 bg-gradient-to-b from-white to-[#F5F5F2] relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden opacity-5 pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-black rounded-full"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-black rounded-full"></div>
      </div>

      <div className="max-w-7xl mx-auto px-2 text-center relative z-10">
        {/* <div className="mb-8 flex justify-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 text-sm font-medium text-gray-500 border-b border-dashed border-gray-300">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              JOIN THE COMMUNITY
            </div>
          </div> */}

        <h2 className="text-[2.75rem] font-averia-bold mb-6 tracking-tight">Stay in the Loop</h2>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          Get notified about new podcasts, business ideas, and features.
          No spam, just pure podcast-to-business inspiration.
        </p>

        <div className="max-w-md mx-auto mb-12">
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button
              type="submit"
              className="px-6 py-3 rounded-full bg-black text-white hover:opacity-90 transition-colors"
            >
              Subscribe
            </button>
          </form>

          {subscriptionStatus === 'success' && (
            <p className="text-sm text-green-600 mt-3 font-medium">
              Thanks for subscribing! We'll be in touch soon.
            </p>
          )}

          {subscriptionStatus === 'error' && (
            <p className="text-sm text-red-600 mt-3 font-medium">
              Something went wrong. Please try again.
            </p>
          )}

          {subscriptionStatus === 'idle' && (
            <p className="text-sm text-gray-500 mt-3">
              By subscribing, you agree to our privacy policy. Unsubscribe anytime.
            </p>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-8 mb-12">
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
              <path d="M9 12 2 2 4 4"></path>
            </svg>
            <span className="text-gray-700">Weekly Updates</span>
          </div>
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
              <path d="M9 12 2 2 4 4"></path>
            </svg>
            <span className="text-gray-700">Exclusive Content</span>
          </div>
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
              <path d="M9 12 2 2 4 4"></path>
            </svg>
            <span className="text-gray-700">Early Access</span>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-8">
          <h3 className="text-lg font-semibold mb-4">Connect With Us</h3>
          <div className="flex justify-center gap-6">
            <a href="https://x.com/talktopods" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-black transition-colors">
              <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 512 512">
                <path d="M0 0 C0.87736816 0.16628906 1.75473633 0.33257812 2.65869141 0.50390625 C35.25005096 6.93288677 64.1196903 20.93416198 88 44 C88.91394531 44.85078125 89.82789063 45.7015625 90.76953125 46.578125 C119.59348782 74.1237659 137.61856081 116.27171425 139 156 C139.6092568 186.88798078 130.58936457 212.79185919 118.26416016 240.64944458 C96.79155704 288.28455623 96.79155704 288.28455623 88.8581543 339.33813477 C88.85467987 340.3536441 88.85120544 341.36915344 88.84762573 342.41543579 C88.84029856 344.61104859 88.83493707 346.80666865 88.83133507 349.00229073 C88.82414206 352.47847057 88.8084564 355.95451151 88.78999329 359.4306488 C88.73764105 369.31076214 88.68951418 379.19079567 88.67407227 389.07104492 C88.6639963 395.12226042 88.63468929 401.1731204 88.59275246 407.22419548 C88.58044065 409.52743785 88.57532152 411.83073039 88.57755089 414.13400459 C88.57990152 417.35347695 88.55775709 420.5719257 88.5300293 423.79125977 C88.53663071 424.74051834 88.54323212 425.68977692 88.55003357 426.6678009 C88.47062702 432.28283144 87.77419903 435.56064815 84 440 C78.93498025 444.46204121 75.93538973 445.14954962 69.33444214 445.15390015 C68.533918 445.1582824 67.73339386 445.16266465 66.90861142 445.1671797 C64.21104462 445.18059423 61.5135105 445.18675256 58.81591797 445.19287109 C56.88773958 445.20077009 54.95956267 445.20903892 53.03138733 445.21765137 C46.6850018 445.24389909 40.33860959 445.25918367 33.9921875 445.2734375 C31.80875774 445.27879641 29.62532802 445.28417392 27.44189835 445.28956985 C18.35907247 445.31120432 9.27624838 445.32994075 0.19340324 445.34119225 C-12.82838384 445.35742821 -25.84987235 445.39059115 -38.87154686 445.44759309 C-48.02407815 445.4862928 -57.17652959 445.5058251 -66.32914054 445.51111281 C-71.79679371 445.51476675 -77.26412862 445.52679255 -82.73169327 445.55921555 C-87.87815637 445.58929576 -93.02410868 445.59560801 -98.17064095 445.58383179 C-100.05721885 445.58361104 -101.94382043 445.59191728 -103.83031654 445.60948563 C-106.41166975 445.63221167 -108.99117618 445.62386723 -111.57254028 445.60800171 C-112.31439013 445.6218838 -113.05623997 445.63576588 -113.82057011 445.65006864 C-119.56123622 445.56348295 -123.55448975 443.60970019 -128 440 C-131.59212617 435.0780304 -131.39768814 430.30122102 -131.29296875 424.4453125 C-131.28872391 423.54376434 -131.28447906 422.64221619 -131.28010559 421.71334839 C-131.26339058 418.85025435 -131.22575192 415.98787921 -131.1875 413.125 C-131.17244387 411.17709354 -131.15875709 409.22917598 -131.14648438 407.28125 C-131.11351058 402.52055878 -131.06177813 397.76040104 -131 393 C-132.05805344 392.83387711 -132.05805344 392.83387711 -133.13748169 392.66439819 C-141.1032547 391.37946522 -148.98146256 389.80600396 -156.8671875 388.1015625 C-158.78472336 387.68721451 -158.78472336 387.68721451 -160.74099731 387.26449585 C-163.42228713 386.68373451 -166.10288771 386.09988869 -168.78320312 385.51464844 C-172.20351908 384.76877144 -175.62684965 384.03779702 -179.05102348 383.30986977 C-182.34633382 382.60564795 -185.63768139 381.88356931 -188.9296875 381.1640625 C-190.15162811 380.90662262 -191.37356873 380.64918274 -192.63253784 380.38394165 C-198.58476935 379.06265239 -203.25961442 377.90438148 -208 374 C-208.86625 373.319375 -208.86625 373.319375 -209.75 372.625 C-213.63767352 367.57102443 -213.30423587 361.3753047 -213.31884766 355.32275391 C-213.32889328 354.36602783 -213.3389389 353.40930176 -213.34928894 352.42358398 C-213.38007549 349.27458269 -213.39714362 346.12566626 -213.4140625 342.9765625 C-213.43277777 340.78794629 -213.45235145 338.59933727 -213.4727478 336.41073608 C-213.52403693 330.66017904 -213.56368 324.90959862 -213.60089111 319.15893555 C-213.64095085 313.28688792 -213.69203424 307.41493731 -213.7421875 301.54296875 C-213.83890873 290.02871401 -213.92332579 278.51440446 -214 267 C-215.14831299 266.44856323 -215.14831299 266.44856323 -216.31982422 265.88598633 C-219.20896316 264.4981094 -222.09775534 263.10951382 -224.98632812 261.72045898 C-226.22740444 261.1238247 -227.46861147 260.52746224 -228.70996094 259.93139648 C-232.81053643 257.96225906 -236.90647677 255.98375217 -241 254 C-242.49805176 253.27937378 -242.49805176 253.27937378 -244.02636719 252.54418945 C-245.36296387 251.89196411 -245.36296387 251.89196411 -246.7265625 251.2265625 C-247.49822754 250.85225098 -248.26989258 250.47793945 -249.06494141 250.09228516 C-252.91027261 247.92170582 -254.49693889 245.0779881 -256.3125 241.1875 C-259.16376575 227.96799516 -248.20961664 216.06316031 -241.50390625 205.5390625 C-236.75978025 198.07204161 -232.14694274 190.52403573 -227.52001953 182.98413086 C-226.55298096 181.4083728 -226.55298096 181.4083728 -225.56640625 179.80078125 C-224.95667969 178.79273438 -224.34695312 177.7846875 -223.71875 176.74609375 C-222 174 -222 174 -220.421875 171.86328125 C-216.27432314 166.16185785 -216.52372857 161.25437635 -216.875 154.375 C-217.89522561 112.47485831 -202.78133383 71.57686658 -174 41 C-173.47277344 40.40832031 -172.94554687 39.81664062 -172.40234375 39.20703125 C-131.96492125 -4.33300679 -54.860928 -10.56257397 0 0 Z M-86 33 C-86.74217773 33.13921875 -87.48435547 33.2784375 -88.24902344 33.421875 C-110.2802504 37.62385736 -131.31671204 46.85080748 -148 62 C-148.69222656 62.59941406 -149.38445312 63.19882813 -150.09765625 63.81640625 C-169.33204468 81.52122105 -178.95854918 109.85016075 -183 135 C-183.10368896 135.62962646 -183.20737793 136.25925293 -183.31420898 136.90795898 C-184.16573988 143.00143696 -183.89391101 148.9657126 -183.67797852 155.10131836 C-182.97221325 177.53396746 -182.97221325 177.53396746 -190.54394531 187.0546875 C-193.83596622 191.45288042 -196.56287586 196.26109677 -199.4375 200.9375 C-200.73609414 203.01308872 -202.03683792 205.08733424 -203.33984375 207.16015625 C-207.94553708 214.50167861 -207.94553708 214.50167861 -212.5 221.875 C-213.06932251 222.78366821 -213.06932251 222.78366821 -213.65014648 223.71069336 C-214.49645034 225.10408614 -215.25888225 226.54792626 -216 228 C-214.47101356 231.05797289 -212.04859227 231.47665716 -208.953125 232.828125 C-207.70288939 233.38619122 -206.45289716 233.94480292 -205.203125 234.50390625 C-203.23323105 235.37686074 -201.26158745 236.24496936 -199.28515625 237.10302734 C-197.37632505 237.93502042 -195.47709568 238.78641003 -193.578125 239.640625 C-192.43762695 240.14110352 -191.29712891 240.64158203 -190.12207031 241.15722656 C-185.94380157 243.62341146 -183.19788731 246.60422538 -181 251 C-180.87692886 254.08106328 -180.81418925 257.14093958 -180.79467773 260.22314453 C-180.78477814 261.1792865 -180.77487854 262.13542847 -180.76467896 263.12054443 C-180.7330088 266.29127499 -180.70828473 269.46202054 -180.68359375 272.6328125 C-180.66302899 274.8271423 -180.64203265 277.0214681 -180.62062073 279.21578979 C-180.56535073 284.99929568 -180.51596321 290.78283761 -180.46777344 296.56640625 C-180.41759435 302.46487649 -180.36188956 308.36329421 -180.30664062 314.26171875 C-180.19899879 325.841101 -180.09756723 337.42052879 -180 349 C-178.67028419 349.28068832 -178.67028419 349.28068832 -177.31370544 349.56704712 C-168.93770227 351.33912284 -160.5664392 353.13183697 -152.19978237 354.94754982 C-147.89877683 355.88018916 -143.59638968 356.80491436 -139.28979492 357.71142578 C-135.12940629 358.5874694 -130.97403501 359.48446466 -126.82124519 360.39582443 C-125.24052051 360.73891961 -123.65816371 361.07458683 -122.07421684 361.40248489 C-107.2307695 364.48433965 -107.2307695 364.48433965 -102.14881897 369.14755249 C-95.22161352 380.31753212 -98.21508492 404.68711256 -98 412 C-47.51 412 2.98 412 55 412 C55.20961249 384.83726284 55.20961249 384.83726284 55.38793945 357.67431641 C55.43786621 352.36010742 55.43786621 352.36010742 55.4650116 349.85385132 C55.4809135 348.14384945 55.49136682 346.4337905 55.49711609 344.72372437 C55.66255372 301.18084142 72.98779715 261.06494212 90.19873047 221.82922363 C106.06441397 185.60934744 110.89946663 153.0547339 96.62719727 115.27026367 C91.27583119 101.80642839 84.32885912 89.10892411 75 78 C73.90558594 76.69675781 73.90558594 76.69675781 72.7890625 75.3671875 C50.12172255 49.35939746 18.85319966 35.45221859 -15 31 C-15.67804688 30.9057373 -16.35609375 30.81147461 -17.0546875 30.71435547 C-25.91132806 29.64735933 -34.8427446 29.75715391 -43.75 29.75 C-45.05290985 29.74848938 -45.05290985 29.74848938 -46.38214111 29.74694824 C-59.80180484 29.75746643 -72.80798235 30.35132142 -86 33 Z " fill="#000000" transform="translate(315,35)" />
                <path d="M0 0 C0.83660156 0.32871094 1.67320312 0.65742188 2.53515625 0.99609375 C15.41849779 6.75078572 24.0385687 17.30856086 29.921875 29.80078125 C32.94594763 38.77438807 33.29845054 47.79681145 33.265625 57.19140625 C33.26753845 58.41729965 33.2694519 59.64319305 33.27142334 60.90623474 C33.2727765 63.48200691 33.26914123 66.05778609 33.26074219 68.63354492 C33.25002138 72.55468177 33.26067231 76.47536149 33.2734375 80.39648438 C33.27211593 82.91145933 33.26955359 85.42643401 33.265625 87.94140625 C33.26967346 89.10209122 33.27372192 90.26277618 33.27789307 91.45863342 C33.18697194 107.39386814 29.25156658 121.27385065 18 133 C7.40373731 143.16740889 -5.2725136 147.91383545 -19.890625 148.03125 C-35.42927635 147.61495316 -48.78334948 142.73816018 -59.75 131.4375 C-70.87639748 118.29356905 -73.31385488 104.49442208 -73.30078125 87.6953125 C-73.30506638 86.45433807 -73.3093515 85.21336365 -73.31376648 83.93478394 C-73.31947835 81.3245301 -73.32003341 78.71426035 -73.31567383 76.10400391 C-73.31251458 72.14327449 -73.3360119 68.1832936 -73.36132812 64.22265625 C-73.36360173 61.67187593 -73.36430433 59.12109363 -73.36328125 56.5703125 C-73.372491 55.40323761 -73.38170074 54.23616272 -73.39118958 53.03372192 C-73.30310343 37.12822758 -69.32025634 23.25539502 -58.1875 11.5625 C-42.82684996 -2.7741067 -19.83415672 -7.83845874 0 0 Z M-35.875 36.875 C-40.45123543 43.60475798 -40.31998828 49.97978574 -40.3359375 57.86328125 C-40.3425943 59.00983688 -40.3492511 60.15639252 -40.35610962 61.33769226 C-40.36621656 63.75921021 -40.37092725 66.1807559 -40.37060547 68.60229492 C-40.37497261 72.28937152 -40.41129868 75.97524481 -40.44921875 79.66210938 C-40.45508863 82.02082926 -40.45905688 84.37955481 -40.4609375 86.73828125 C-40.47530853 87.83231766 -40.48967957 88.92635406 -40.50448608 90.05354309 C-40.46138031 97.34649722 -39.51292176 103.84942248 -34.65878296 109.58467102 C-30.53122034 113.3799208 -26.14190075 114.95945935 -20.578125 115.03125 C-13.4805475 114.60109379 -8.73787183 112.81186837 -3.6875 107.625 C0.52332137 101.07483342 0.30701255 94.64446879 0.30078125 87.1015625 C0.30720894 85.37817154 0.30720894 85.37817154 0.31376648 83.6199646 C0.31949884 81.1942409 0.32001812 78.76850002 0.31567383 76.34277344 C0.31251697 72.64488171 0.33597718 68.94779274 0.36132812 65.25 C0.36360163 62.88802184 0.36430438 60.52604153 0.36328125 58.1640625 C0.372491 57.06486481 0.38170074 55.96566711 0.39118958 54.8331604 C0.34949258 47.86359329 -0.06622962 41.60116839 -4.6484375 35.9765625 C-10.52670008 31.37502664 -14.86562691 29.50721731 -22.4375 29.6875 C-27.93509308 30.35793818 -32.18306557 32.78481025 -35.875 36.875 Z " fill="#000000" transform="translate(292,129)" />
                <path d="M0 0 C5.20791398 2.90628907 8.13156498 6.41750867 9.7734375 12.1640625 C9.96607418 13.95130281 10.10954033 15.7443326 10.2109375 17.5390625 C10.68297983 25.10222969 13.49635149 31.40849175 19.0859375 36.6015625 C25.70285934 42.18511116 33.46258396 42.62240989 41.703125 42.59765625 C42.73349884 42.6028981 43.76387268 42.60813995 44.82546997 42.61354065 C46.99545393 42.61993642 49.16547267 42.61865572 51.33544922 42.61010742 C54.63182176 42.60160536 57.92645479 42.63038202 61.22265625 42.66210938 C63.34114532 42.66372199 65.45963742 42.66314271 67.578125 42.66015625 C68.55135712 42.67139023 69.52458923 42.68262421 70.52731323 42.69419861 C78.14429446 42.61772916 85.08969114 41.06913077 91.17578125 36.26953125 C95.90459653 30.3615203 98.7133282 25.41303814 99.2109375 17.7265625 C99.66529361 11.59275506 100.88492547 6.84345149 105.5 2.5625 C110.40221497 -0.93350191 114.85309226 -1.82632983 120.7734375 -0.8359375 C126.04665903 0.98940841 128.57034836 3.77125453 131.7734375 8.1640625 C135.63474475 19.74798425 132.09210036 33.42652185 127.2734375 44.1640625 C119.61750384 58.67605616 106.85084592 68.30062589 91.7734375 74.1640625 C84.8224917 76.29812481 77.99795394 76.4112893 70.7734375 76.1640625 C70.7734375 78.8040625 70.7734375 81.4440625 70.7734375 84.1640625 C71.4746875 84.12925781 72.1759375 84.09445312 72.8984375 84.05859375 C79.31544046 83.87549992 84.01592623 84.23480238 89.7734375 87.1640625 C93.78125155 91.27734534 96.05751019 95.20267838 96.1484375 100.9765625 C96.01670542 106.23809664 95.01877542 109.00337286 91.7734375 113.1640625 C85.34042677 118.12321492 79.10969023 117.74630002 71.2109375 117.6953125 C69.90568115 117.6991394 68.6004248 117.70296631 67.25561523 117.70690918 C64.50192172 117.71278291 61.74874972 117.70431131 58.99511719 117.68554688 C55.48124166 117.66275725 51.96858846 117.67589198 48.45475769 117.69983673 C45.081722 117.71784041 41.70900782 117.70520206 38.3359375 117.6953125 C37.08159912 117.70340942 35.82726074 117.71150635 34.53491211 117.71984863 C27.75380623 117.64727508 23.21794816 117.36118875 17.7734375 113.1640625 C14.05143917 108.39226977 13.11548551 104.62847623 13.390625 98.59375 C14.22545795 93.2951164 16.53845351 90.14175816 20.7734375 86.7890625 C25.01812547 84.48985652 28.2204177 84.03836487 33.0234375 84.1015625 C34.36148438 84.11509766 34.36148438 84.11509766 35.7265625 84.12890625 C36.40203125 84.14050781 37.0775 84.15210938 37.7734375 84.1640625 C37.7734375 81.5240625 37.7734375 78.8840625 37.7734375 76.1640625 C36.89945313 76.21626953 36.89945313 76.21626953 36.0078125 76.26953125 C21.402917 76.75176837 7.35113294 71.35005156 -3.6640625 61.7890625 C-16.20667233 49.63712413 -23.2043261 35.92290424 -23.6015625 18.2890625 C-23.63906919 12.40051166 -22.79515817 7.98015307 -18.7265625 3.51953125 C-12.99690937 -0.94183928 -7.01032382 -2.63194613 0 0 Z " fill="#000000" transform="translate(217.2265625,249.8359375)" />
              </svg>
            </a>
            {/* TODO: Add LinkedIn */}
            {/* <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-black transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
              </a>
              {/* TODO: Add GitHub */}
            {/* <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-black transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
                  <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
                </svg>
              </a>  */}
          </div>
        </div>
      </div>
    </section>
  );
}

// Footer Component
function Footer() {
  return (
    <footer className="py-8 text-center text-gray-600 text-sm border-t border-gray-200 bg-[#F5F5F2] shadow-[0_-1px_2px_0_rgba(0,0,0,0.05)]">
      Made with love ❤️ and crippling procrastination by Omee
    </footer>
  );
}

function App() {
  // Initialize auth redirects
  useEffect(() => {
    setupAuthRedirects();
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <AnalyticsTracker />
          <AppContent />
          {/* <FeedbackWidget /> - commented out: floating button was obstructing chat UI */}
          <Toaster />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
